import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image, Package, CheckCircle, XCircle, Clock, AlertTriangle, AlertCircle, Layers, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import SupplierSidebar from '../../components/supplier/SupplierSidebar';
import SupplierHeader from '../../components/supplier/SupplierHeader';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { formatDateTime, STAGES, doesSupplierStageSubmissionRequireAction, isSupplierStageSubmissionPendingReview, isSupplierStageSubmissionChangesRequested, getSupplierStageSubmissionFromArtwork, isSupplierArtworkCompleted } from '../../utils/helpers';
import './SupplierDashboard.css';

export default function SupplierDashboard() {
  const { supplier, logout } = useAuth();
  const navigate = useNavigate();
  const [artworks, setArtworks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [activeStageTab, setActiveStageTab] = useState(0); // 0 = Total Artworks

  useEffect(() => {
    (async () => {
      try {
        const [artRes, notifRes] = await Promise.all([
          api.get('/artworks/supplier/my-artworks?summary=true'),
          api.get('/notifications')
        ]);
        setArtworks(artRes.data);
        setNotifications(notifRes.data);
        setUnread(notifRes.data.filter(n => !n.isRead).length);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    const handleRefresh = async () => {
      try {
        const [artRes, notifRes] = await Promise.all([
          api.get('/artworks/supplier/my-artworks?summary=true'),
          api.get('/notifications')
        ]);
        setArtworks(artRes.data);
        setNotifications(notifRes.data);
        setUnread(notifRes.data.filter(n => !n.isRead).length);
        toast.success('Data refreshed');
      } catch {
        toast.error('Failed to refresh data');
      }
    };

    window.addEventListener('navigationRefresh', handleRefresh);
    return () => window.removeEventListener('navigationRefresh', handleRefresh);
  }, []);

  useEffect(() => {
    if (showLogoutConfirm) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showLogoutConfirm]);

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnread(0);
    } catch {}
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    logout();
    navigate('/supplier/login');
  };

  // getActionRequired was removed — logic consolidated inline where needed

  const supplierId = supplier?._id || supplier?.id;
  const stats = {
    total: artworks.length,
    inReview: artworks.filter((a) => {
      const stageNum = a.currentStage;
      if (![3, 4].includes(stageNum)) return false;
      return isSupplierStageSubmissionPendingReview(a, stageNum, supplierId);
    }).length,
    actionNeeded: artworks.filter((a) => {
      const stageNum = a.currentStage;
      if (![3, 4].includes(stageNum)) return false;
      return doesSupplierStageSubmissionRequireAction(a, stageNum, supplierId) && !isSupplierStageSubmissionChangesRequested(a, stageNum, supplierId);
    }).length,
    changesRequested: artworks.filter((a) => {
      const stageNum = a.currentStage;
      if (![3, 4].includes(stageNum)) return false;
      return isSupplierStageSubmissionChangesRequested(a, stageNum, supplierId);
    }).length,
    completed: artworks.filter((a) => {
      return isSupplierArtworkCompleted(a, supplierId);
    }).length,
  };

  const stageTabs = [
    { stage: 3, label: 'Position Proof Stage', icon: Layers, color: '#047857', background: '#ecfdf5' },
    { stage: 4, label: 'Colour Proof / Chromalin Stage', icon: Package, color: '#0369a1', background: '#f0f9ff' },
    { stage: 5, label: 'Changes Requested', icon: RotateCcw, color: '#c2410c', background: '#fff7ed' },
    { stage: 6, label: 'Completed Artwork', icon: CheckCircle, color: '#15803d', background: '#f0fdf4' }
  ];

  const supplierKpiCards = [
    { stage: 0, label: 'Total Artworks', count: stats.total, icon: Package, color: '#475569', background: '#f1f5f9' },
    { stage: 5, label: 'Changes Requested', count: stats.changesRequested, icon: RotateCcw, color: '#c2410c', background: '#fff7ed' },
    ...stageTabs.filter(tab => tab.stage === 3 || tab.stage === 4),
    { stage: 7, label: 'Action Required', count: stats.actionNeeded, icon: AlertTriangle, color: '#b45309', background: '#fffbeb' },
    { stage: 8, label: 'Under Review', count: stats.inReview, icon: Clock, color: '#be123c', background: '#fff1f2' },
    { stage: 6, label: 'Completed', count: stats.completed, icon: CheckCircle, color: '#15803d', background: '#f0fdf4' }
  ];

  const hasSupplierStage4Submission = (artwork) => Boolean(getSupplierStageSubmissionFromArtwork(artwork, 4, supplierId));
  const shouldShowInStage3 = (artwork) => {
    if (artwork.currentStage === 3) return true;
    if (artwork.currentStage === 4 && !hasSupplierStage4Submission(artwork)) return true;
    return false;
  };

  const stageArtworks = artworks.filter((a) => {
    if (activeStageTab === 0) return true;

    if (activeStageTab === 5) {
      const stageNum = a.currentStage;
      if (![3, 4].includes(stageNum)) return false;
      const supplierSubmission = getSupplierStageSubmissionFromArtwork(a, stageNum, supplierId);
      if (!supplierSubmission) return false;
      return isSupplierStageSubmissionChangesRequested(a, stageNum, supplierId);
    }

    if (activeStageTab === 7 || activeStageTab === 8) {
      const stageNum = a.currentStage;
      if (![3, 4].includes(stageNum)) return false;
      if (activeStageTab === 7) {
        return doesSupplierStageSubmissionRequireAction(a, stageNum, supplierId) && !isSupplierStageSubmissionChangesRequested(a, stageNum, supplierId);
      }
      return isSupplierStageSubmissionPendingReview(a, stageNum, supplierId);
    }

    if (activeStageTab === 6) {
      return isSupplierArtworkCompleted(a, supplierId);
    }

    if (activeStageTab === 3) {
      return shouldShowInStage3(a);
    }

    if (activeStageTab === 4) {
      return a.currentStage === 4 && hasSupplierStage4Submission(a);
    }

    return false;
  });

  const activeStageLabel = supplierKpiCards.find(t => t.stage === activeStageTab)?.label;
  const uploadColumnLabel = activeStageTab === 3
    ? 'Uploaded Position Proof'
    : activeStageTab === 4
      ? 'Uploaded Colour Proof'
      : activeStageTab === 6
        ? 'Finalized'
        : 'Uploaded Proof';

  const getTabCount = (tab) => {
    if (tab.stage === 0) return stats.total;
    if (tab.stage === 7) return stats.actionNeeded;
    if (tab.stage === 8) return stats.inReview;
    if (tab.stage === 5) return stats.changesRequested;
    if (tab.stage === 6) return stats.completed;
    if (tab.stage === 3) {
      return artworks.filter((a) => shouldShowInStage3(a)).length;
    }
    if (tab.stage === 4) {
      return artworks.filter((a) => a.currentStage === 4 && hasSupplierStage4Submission(a)).length;
    }
    return 0;
  };

  return (
    <div className="page-layout">
      <SupplierSidebar
        onLogout={handleLogout}
      />
      <div className="main-content">
        <SupplierHeader
          title={`Welcome back, ${supplier?.name} 👋`}
          subtitle="Here are your assigned artworks requiring attention"
          unread={unread}
          notifications={notifications}
          onMarkAllRead={markAllRead}
        />
        <div className="page-body">

          {/* Artworks */}
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--gray-100)' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Assigned Artworks</h3>
            </div>

            <div className="supplier-stage-kpis" aria-label="Artwork stage filters">
              {supplierKpiCards.map(({ stage, label, icon: Icon, color, background, count }) => (
                <button
                  key={stage}
                  type="button"
                  className={`supplier-stage-kpi ${activeStageTab === stage ? 'active' : ''}`}
                  onClick={() => setActiveStageTab(stage)}
                  aria-pressed={activeStageTab === stage}
                >
                  <span className="supplier-stage-kpi-icon" style={{ color, background }}><Icon size={18} strokeWidth={2.2} /></span>
                  <span>
                    <span className="supplier-stage-kpi-label">{label}</span>
                    <span className="supplier-stage-kpi-value">{count ?? getTabCount({ stage })}</span>
                  </span>
                </button>
              ))}
            </div>

            {loading ? (
              <div style={{ padding: 48, textAlign: 'center' }}>
                <div className="spinner spinner-dark" style={{ width: 28, height: 28, margin: '0 auto' }} />
              </div>
            ) : stageArtworks.length === 0 ? (
              <div key={activeStageTab} className="empty-state">
                <Image size={40} />
                <h3>No artworks in {activeStageLabel}</h3>
                <p>Artworks currently at this stage will appear here for you to review and submit proofs.</p>
              </div>
            ) : (
              <div key={activeStageTab} className="supplier-stage-table-wrap">
                <table className="supplier-stage-table">
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Artwork</th>
                      <th>Stage</th>
                      <th>Last Updated</th>
                      <th>{uploadColumnLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stageArtworks.map((a, index) => {
                      const stage = a[`stage${a.currentStage}`];
                      // For supplier-specific uploads, files live on a submission object
                      const supplierId = supplier?._id || supplier?.id;
                      const supplierSubmission = (stage?.submissions || []).find(s => String(s.supplierId) === String(supplierId));
                      const isUploaded = Boolean(supplierSubmission?.artworkUrl || supplierSubmission?.artworkData || supplierSubmission?.artworkFileName);
                      return (
                        <tr
                          key={a._id}
                          className="supplier-stage-row"
                          onClick={() => navigate(`/supplier/artworks/${a._id}`)}
                        >
                          <td>{index + 1}</td>
                          <td>
                            <div className="supplier-stage-row-artwork">
                              <div className="supplier-artwork-thumb" style={{ width: 40, height: 40 }}>
                                {stage?.artworkUrl
                                  ? <img src={stage.artworkUrl} alt={a.title} />
                                  : <Image size={18} />
                                }
                              </div>
                              <span className="supplier-artwork-title">{a.title}</span>
                            </div>
                          </td>
                          <td>
                            <span className={`badge badge-stage${a.currentStage}`}>
                              Stage {a.currentStage}: {STAGES[a.currentStage]?.short}
                            </span>
                          </td>
                          <td className="supplier-artwork-date">{formatDateTime(a.updatedAt)}</td>
                          <td>
                            {isUploaded ? (
                              <CheckCircle size={20} color="var(--success)" />
                            ) : (
                              <XCircle size={20} color="var(--danger)" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          pointerEvents: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <AlertCircle size={20} color="#C8102E" />
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111' }}>
                Sign Out?
              </h3>
            </div>
            <p style={{ margin: '0 0 24px 0', color: '#666', fontSize: '0.9375rem' }}>
              Are you sure you want to sign out? You'll need to log in again to access your account.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#e5e5e5'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#C8102E',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#a00826'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#C8102E'}
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}